/* eslint-disable react/no-unstable-nested-components */
import React from 'react';
import ReactModal from 'react-modal';

import { ZIndex } from '@rover/constants/src/styles/ZIndex';
import { Box, Flex } from '@rover/kibble/core';
import { useModalOpenTrackerEffect } from '@rover/kibble/utils/modalOpenTracker';

import Content from './Content/Content';
import {
  focusDialogStart,
  ModalCommonProps,
  resolveTitleProps,
  showPortalToFocusTrap,
  useHideOutsideDialog,
  usePreventScroll,
  useReactModalSetup,
} from './Modal.common';
import Overlay from './Overlay';

/** Props for web Modal component */
export type Props = ModalCommonProps & {
  /**
   * Doesn't reach the dialog: Modal moves these, and any `aria-*` prop, onto an inner element
   * without the `aria-` prefix.
   */
  aria?: Record<string, string>;
  /** The `id` given to the title, which names the dialog. Defaults to a generated id. */
  headerId?: string;
  /**
   * A fixed height, as a CSS length, for the column of title, body and buttons. The body stretches
   * to fill it.
   */
  contentWrapperHeight?: string;
  /** Receives the dialog element once it mounts. */
  modalRef?: (instance: HTMLElement) => void;
};

/** A dialog over a dimmed page, with a title, a body and a row of buttons. */
export default function Modal(modalProps: Props): JSX.Element {
  const {
    children,
    isOpen,
    onRequestClose,
    isDismissible = true,
    shouldCloseOnOverlayClick,
    maxHeight,
    title,
    accessibilityLabel,
    headerId,
    titleSize,
    showCloseIcon,
    closeIconSize,
    appElementSelector = '#base-content',
    contentProps = {},
    titleProps,
    contentWrapperHeight,
    overlaySx = {},
    modalRef,
    ...other
  } = resolveTitleProps(modalProps);
  const { staticHeaderId, modalId } = useReactModalSetup(appElementSelector);
  const actualHeaderId = headerId || staticHeaderId;
  usePreventScroll(isOpen, modalId);
  const hideOutsideDialog = useHideOutsideDialog(isOpen);
  useModalOpenTrackerEffect(isOpen);

  // ReactModal takes all aria-* props in an `aria` object.
  const ariaProps = other.aria || {};
  Object.keys(other).forEach((key) => {
    if (key.startsWith('aria-')) {
      ariaProps[key.substring(5)] = other[key];
      delete other[key];
    }
  });

  return (
    <ReactModal
      aria={{ labelledby: (title || headerId) && !accessibilityLabel ? actualHeaderId : undefined }}
      contentLabel={accessibilityLabel}
      contentRef={modalRef}
      onAfterOpen={(options) => {
        showPortalToFocusTrap(options);
        hideOutsideDialog(options);
        focusDialogStart(options);
      }}
      className="RoverModalContent"
      closeTimeoutMS={150}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick ?? isDismissible}
      shouldCloseOnEsc={isDismissible || Boolean(showCloseIcon)}
      overlayClassName="RoverModalOverlay"
      overlayElement={(props, overlayChildren): JSX.Element => (
        <Overlay data-testid="modal-overlay" {...props} sx={overlaySx}>
          {overlayChildren}
        </Overlay>
      )}
    >
      <Box {...ariaProps}>
        <Flex
          variant="elevation_high"
          background="primary"
          borderRadius="primary"
          height="auto"
          maxWidth="600px"
          minHeight="100px"
          minWidth="335px"
          maxHeight="100vh"
          padding="6x"
          width="auto"
          zIndex={ZIndex.MODAL.toString()}
          {...(maxHeight && { flexDirection: 'column', maxHeight })}
          {...contentProps}
        >
          <Content
            headerId={actualHeaderId}
            title={title}
            titleSize={titleSize}
            onRequestClose={onRequestClose}
            showCloseIcon={showCloseIcon}
            closeIconSize={closeIconSize}
            titleProps={titleProps}
            contentWrapperHeight={contentWrapperHeight}
            scrollsBody={Boolean(maxHeight)}
            {...other}
          >
            {children}
          </Content>
        </Flex>
      </Box>
    </ReactModal>
  );
}
